import React, { useEffect, useState } from "react";
import { Input } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { getProductInfo } from "@ifa/utils";

interface ProductSelectInputProps {
  value?: string; // productCode
  initialName?: string; // productName (optional)
  placeholder?: string;
  context: { type: string; index: number; rIndex?: number };
  style?: React.CSSProperties;
  onOpenSelector?: (context: { type: string; index: number; rIndex?: number }, productType?: string) => void;
  productType?: string;
  companyCode?: string; // 保险公司代码，用于过滤附加险产品
  disabled?: boolean; // 是否禁用
}

/**
 * ProductSelectInput Component
 *
 * Handles product selection navigation and echoes product name.
 * If product name is not provided but code is present, it fetches the name using getProductInfo.
 */
export const ProductSelectInput: React.FC<ProductSelectInputProps> = ({ value, initialName, placeholder, context, style, onOpenSelector, productType, companyCode, disabled = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [productName, setProductName] = useState<string>("");

  useEffect(() => {
    // If we have an initial name passed (e.g. from state), use it
    if (initialName) {
      setProductName(initialName);
      return;
    }

    // If we have a value (code) but no name, fetch it
    if (value) {
      const fetchProductName = async () => {
        try {
          const res = await getProductInfo(value);
          if (res && res.productName) {
            setProductName(res.productName);
          } else {
            // Fallback to code if name not found
            setProductName(value);
          }
        } catch (error) {
          console.error("Failed to fetch product info:", error);
          setProductName(value);
        }
      };
      fetchProductName();
    } else {
      setProductName("");
    }
  }, [value, initialName]);

  const handleClick = () => {
    if (disabled) return; // 如果禁用，不執行任何操作
    if (onOpenSelector) {
      onOpenSelector(context, productType);
    } else {
      navigate("/product-select", {
        state: {
          ...location.state,
          selectMode: true,
          from: location.pathname,
          selectionContext: context,
          productType: productType,
          companyCode: companyCode, // 传递公司代码用于过滤
        },
      });
    }
  };

  return (
    <Input
      placeholder={placeholder || "請選擇"}
      value={productName || value || ""}
      readOnly
      onClick={handleClick}
      suffix={<RightOutlined />}
      style={{ ...style, cursor: disabled ? "not-allowed" : "pointer" }}
      disabled={disabled}
    />
  );
};
